import { Configuration } from '~entities/configuration/model/configuration.types';
import { Offer } from '../model/offer.types';
import { Price } from '~entities/price/model/price.types';
import { Room, RoomFeature } from '~entities/room/model/room.types';

// Stable comparison key for a price; cents-rounded to avoid float noise so two
// equal amounts always collide. `undefined` for offers without a price (those
// are never treated as duplicates).
const priceKey = (price?: Price): string | undefined =>
  price ? `${price.currency}:${Math.round(price.amount * 100)}` : undefined;

/**
 * Hides plain room offers that a package already covers at the same price.
 *
 * When a room is bookable both as a plain room offer (`type: 'ROOM'`) and as a
 * package offer (`type: 'PACKAGE'`) for the exact same price, the package is the
 * better deal (it bundles extras for the same money), so the plain room offer is
 * dropped and the package remains. All package offers are kept — including
 * several different packages at the same price; only the regular room is hidden.
 *
 * Expects the offers of a single room (same `roomId`): matching is by price
 * alone, since price is the comparable a guest weighs between the two cards.
 *
 * @param offers The offers of one room.
 * @returns The offers with room duplicates of a same-priced package removed.
 */
export const hideRoomOffersDuplicatedByPackages = (
  offers: Offer[],
): Offer[] => {
  const packagePriceKeys = new Set(
    offers
      .filter(offer => offer.type === 'PACKAGE')
      .map(offer => priceKey(offer.price))
      .filter((key): key is string => key !== undefined),
  );

  if (packagePriceKeys.size === 0) return offers;

  return offers.filter(offer => {
    if (offer.type !== 'ROOM') return true;
    const key = priceKey(offer.price);
    return key === undefined || !packagePriceKeys.has(key);
  });
};

/**
 * Whether the guest may book this offer, as opposed to only inquiring about it.
 *
 * Gated by two independent flags: a hotel that never takes online bookings
 * (`configuration.bookingEnabled: false`) hides the select CTA on every offer, regardless of
 * what the offer itself reports (samera/samera#3523). Below that gate, `bookable` is undefined
 * against a backend that predates samera/samera#3217, so only an explicit `false` withholds
 * the booking CTA -- an older backend must keep offering both.
 *
 * The `bookable` half is a courtesy check only: `OneBookabilityChecker` recomputes it at
 * checkout (via `BookingValidator`) and is the real gate there. `bookingEnabled` has no such
 * backend re-check today -- this client-side gate is the only thing stopping a direct API call
 * from booking a hotel that has online booking disabled entirely.
 */
export const isOfferBookable = (
  configuration: Pick<Configuration, 'bookingEnabled'>,
  offer: Pick<Offer, 'bookable'>,
): boolean => configuration.bookingEnabled && offer.bookable !== false;

export const getOffersSpecialFeatures =
  (configuration: Configuration) => (offers: Offer[]) => {
    const { rooms } = configuration;

    const offersRooms = offers.reduce((acc, offer) => {
      if (offer.roomId) {
        const room = rooms.find(room => room.id === offer.roomId);
        if (room && !acc.includes(room)) {
          acc.push(room);
        }
      }
      return acc;
    }, [] as Room[]);

    const featureCountMap = new Map<
      number,
      { feature: RoomFeature; count: number }
    >();

    offersRooms.forEach(room => {
      room.features.forEach(feature => {
        if (featureCountMap.has(feature.id)) {
          const count = featureCountMap.get(feature.id)?.count ?? 0;
          featureCountMap.set(feature.id, { feature, count: count + 1 });
        } else {
          featureCountMap.set(feature.id, { feature, count: 1 });
        }
      });
    });

    return Array.from(featureCountMap.values())
      .filter(({ count }) => count === 1)
      .map(({ feature }) => feature);
  };
